import { useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useFetcher, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { cardVariants } from '#app/components/ui/card.tsx'
import { type Command } from '#app/components/ui/command.matcher.ts'
import { CommandPalette } from '#app/components/ui/command.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'
import { AppFrame } from './__app-frame.tsx'
import {
	landingCommands,
	type ThemeMode,
} from './__command-palette.commands.ts'
import { useReveal } from './__use-reveal.ts'

/**
 * The ⌘K palette showpiece on the landing. A global ⌘K / Ctrl-K listener toggles
 * the palette anywhere on the page (esc closes — Radix-managed focus trap), and a
 * demo `AppFrame` surfaces a hint pill that opens the same palette.
 *
 * The slice-① command set is built by the framework-free factory
 * (`__command-palette.commands.ts`): Navigation commands navigate (SPA, in-page
 * anchors), Theme commands re-tint the page through the existing cookie + SSR
 * theme plumbing (`/resources/theme-switch`, optimistic) and fire a `sonner`
 * toast. Tokens only; the palette overlay is a deliberate transient client
 * overlay (ADR 023 exception, documented in `command.tsx`).
 */
export function CommandShowpiece() {
	const { t } = useTranslation(['public', 'common'])
	const ref = useRef<HTMLElement>(null)
	useReveal(ref)

	const [open, setOpen] = useState(false)
	const navigate = useNavigate()
	const themeFetcher = useFetcher()

	/**
	 * No-match suggestion chips — each fires a toast to prove an action ran. Memoized
	 * so the prop stays referentially stable (CommandPalette's `NO_EMPTY_ACTIONS`
	 * default exists for the same reason); it only rebuilds when the locale changes.
	 */
	const emptyActions: Command[] = useMemo(
		() => [
			{
				id: 'suggest-start',
				title: t('landing.command.suggestions.start'),
				group: t('landing.command.suggestions.label'),
				icon: 'envelope-closed',
				run: () => toast.success(t('landing.command.suggestions.start')),
			},
			{
				id: 'suggest-support',
				title: t('landing.command.suggestions.support'),
				group: t('landing.command.suggestions.label'),
				icon: 'question-mark-circled',
				run: () => toast.success(t('landing.command.suggestions.support')),
			},
		],
		[t],
	)

	// Global ⌘K / Ctrl-K toggle — works anywhere on the landing.
	useEffect(() => {
		function onKeyDown(event: KeyboardEvent) {
			if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
				event.preventDefault()
				setOpen((prev) => !prev)
			}
		}
		document.addEventListener('keydown', onKeyDown)
		return () => document.removeEventListener('keydown', onKeyDown)
	}, [])

	/**
	 * Persist a theme change through the existing switch resource. Routing it
	 * through `useFetcher` (action `/resources/theme-switch`) is what lets
	 * `useOptimisticThemeMode` re-tint the whole page live; a toast confirms it.
	 *
	 * No `redirectTo`: that's the no-JS escape hatch (the `<ServerOnly>`-gated
	 * `<Form>` inputs). Sending it from a fetcher makes the action return a
	 * single-fetch redirect to the index `.data` URL that 404s through the splat.
	 */
	function handleTheme(mode: ThemeMode) {
		void themeFetcher.submit(
			{ theme: mode },
			{ method: 'POST', action: '/resources/theme-switch' },
		)
		toast.success(t(`landing.command.themeToast.${mode}`))
	}

	const commands = landingCommands({ onTheme: handleTheme, t })

	return (
		<section
			id="command"
			ref={ref}
			aria-labelledby="command-heading"
			className="container scroll-mt-20 py-24"
		>
			<div className="mx-auto max-w-2xl text-center">
				<p className="text-brand text-body-xs tracking-eyebrow font-semibold uppercase">
					{t('landing.command.eyebrow')}
				</p>
				<h2
					id="command-heading"
					className="tracking-h2 mt-3 text-3xl font-semibold text-balance"
				>
					{t('landing.command.heading')}
				</h2>
				<p className="text-muted-foreground mt-4 text-pretty">
					<Trans
						t={t}
						i18nKey="landing.command.lead"
						components={{
							1: (
								<kbd className="bg-muted text-muted-foreground border-border text-body-2xs rounded-md border px-1.5 py-0.5 font-medium" />
							),
						}}
					/>
				</p>
			</div>

			<div data-reveal className="mx-auto mt-12 max-w-2xl">
				<AppFrame url="opensourced.studio">
					<button
						type="button"
						onClick={() => setOpen(true)}
						className={cn(
							'bg-background text-muted-foreground border-border focus-visible:ring-ring flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2',
							cardVariants.interactive,
						)}
					>
						<Icon name="magnifying-glass" className="shrink-0" />
						<span className="text-body-sm flex-1">
							{t('landing.command.placeholder')}
						</span>
						<kbd className="bg-muted text-muted-foreground border-border text-body-2xs rounded-md border px-1.5 py-0.5 font-medium">
							⌘K
						</kbd>
					</button>
				</AppFrame>
			</div>

			<CommandPalette
				commands={commands}
				open={open}
				onOpenChange={setOpen}
				onNavigate={(href) => navigate(href)}
				emptyActions={emptyActions}
				placeholder={t('landing.command.placeholder')}
			/>
		</section>
	)
}
